import { type ButtonHTMLAttributes, forwardRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared button set (STYLE.md "Buttons"): primary = solid pill in --cta (black in light, white in dark),
 * outline/default = outlined pill, ghost = borderless. Blue is reserved for links, focus and selection.
 */
const VARIANTS = {
  default: 'border-border-strong bg-transparent text-fg hover:bg-hover',
  primary: 'border-transparent bg-cta text-cta-fg hover:bg-cta-hover',
  /** Blue accent button for the rare in-context action that must read as "selected"/link-like. */
  accent: 'border-transparent bg-accent text-accent-fg hover:bg-accent-2',
  outline: 'border-border-strong bg-transparent text-fg hover:bg-hover',
  ghost: 'border-transparent bg-transparent text-muted hover:bg-hover hover:text-fg',
  danger: 'border-danger/40 bg-danger/10 text-danger hover:bg-danger/20',
  success: 'border-success/40 bg-success/10 text-success hover:bg-success/20',
} as const;

const SIZES = {
  sm: 'h-7 px-3 text-xs gap-1 touch:h-10',
  md: 'h-8 px-3.5 text-sm gap-1.5 touch:h-10',
  lg: 'h-10 px-5 text-base gap-2 touch:h-11',
  icon: 'h-8 w-8 justify-center p-0 touch:h-10 touch:w-10',
} as const;

const BASE =
  'inline-flex shrink-0 items-center whitespace-nowrap rounded-lg border font-medium transition-colors disabled:pointer-events-none disabled:opacity-45';

/** Button styling for non-button elements (e.g. router `<Link>`), avoiding <a><button> nesting. */
export function buttonVariants(
  variant: keyof typeof VARIANTS = 'default',
  size: keyof typeof SIZES = 'md',
  className?: string,
) {
  return cn(BASE, VARIANTS[variant], SIZES[size], className);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', loading, className, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

/** Ghost icon button (round hover) for headers, top bar and pane chrome. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean }
>(function IconButton({ label, active, className, children, type = 'button', ...props }, ref) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex h-7 w-7 touch:h-10 touch:w-10 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-hover hover:text-fg disabled:opacity-40',
        active && 'bg-panel-2 text-fg',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

const FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];

/** Braille spinner, like Claude Code's. */
export function Spinner({ className, label }: { className?: string; label?: string }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % FRAMES.length), 80);
    return () => clearInterval(t);
  }, []);
  return (
    <span
      role="status"
      aria-label={label ?? 'Loading'}
      className={cn('inline-block w-[1ch] font-mono text-claude', className)}
    >
      {FRAMES[i]}
    </span>
  );
}
